// @flow
import * as React from 'react';

import {usePrefs} from 'docs/data';
import {useCondition, useData} from 'docs/data/integration-builder';
import {locales} from 'docs/data/integration-builder/data/fixtures/terminal';
import {formatAppearanceObject, formatTerminalPaymentMethods} from 'docs/util';
import type {Condition} from 'docs/data/integration-builder';

import {Line} from './Line';

type ChunkProps = {|
  id: string, // The ID of the primary {% step %} wrapping this Chunk
  steps: Array<string>, // List of all steps this Chunk is active for
  language?: string,
  chunks?: Array<string> | Array<React.Element<any>>,
  when?: Condition,
  unless?: Condition,
|};

export const Chunk = ({
  id,
  steps,
  language,
  when,
  unless,
  chunks,
}: ChunkProps) => {
  const {isVisible} = useCondition();
  const {appearance, activePrice} = useData();
  const {getPref} = usePrefs();
  const toggled = false;
  const client = getPref('client');
  const country = getPref('integration-country');

  if (!chunks || !isVisible({when, unless})) {
    return null;
  }

  return (
    <>
      {chunks.map((chunk, i) => {
        if (typeof chunk === 'string') {
          // By keeping track of whether this is the first or last line in a Chunk
          // we can optimize the scroll when its step is active
          // Note that this currently doesn't work great for nested steps (with long content)
          // that end on the same line that is out of viewport:
          // {% step id="step-2" %}
          // const foo = "bar";
          // {% step id="step-1" %}
          // const bar = "foo";
          // {% /step %}
          // {% /step %}
          const position = {};

          if (i === 0) {
            position[id] = 'first';
          } else if (i === chunks.length - 1) {
            position[id] = 'last';
          }

          /* Replace any dynamic data set on the client, e.g. instaces of a Price or PaymentMethods */
          let content;
          if (activePrice && chunk.match(/{{PRICE_ID}}/)) {
            content = chunk.replace(/{{PRICE_ID}}/, activePrice.id);
          } else if (activePrice && chunk.match(/{{PRICE_LOOKUP_KEY}}/)) {
            content = chunk.replace(
              /{{PRICE_LOOKUP_KEY}}/,
              activePrice.lookup_key,
            );
          } else if (chunk.match(/{{TERMINAL_CURRENCY}}/)) {
            const currency = locales[country]?.currency || 'usd';

            content = chunk.replace(
              /{{TERMINAL_CURRENCY}}/,
              language === 'go' ? currency.toUpperCase() : currency,
            );
          } else if (chunk.match(/{{TERMINAL_LOCATION_([A-Za-z0-9]*)}}/)) {
            // Replaces an instance of {{TERMINAL_LOCATION_*}} placeholder data in the code sample
            // If there is no value for the country, we skip rendering that line entirely
            const match =
              chunk.match(/{{TERMINAL_LOCATION_([A-Za-z0-9]*)}}/) || [];
            const key = match.length > 1 ? match[1] : '';
            const locationData =
              locales[country]?.location || locales.US.location;
            const val = locationData[key.toLowerCase()] || '';

            if (val) {
              content = chunk.replace(`{{TERMINAL_LOCATION_${key}}}`, val);
            }
          } else if (chunk.match(/{{TERMINAL_PAYMENT_METHODS}}/)) {
            // We use the same language key to format server & client Prism snippets
            // however javaclient snippets have different formatting for PM constants
            const formatLang = client === 'java' ? 'javaclient' : language;
            const pmLines = formatTerminalPaymentMethods(formatLang, country);
            if (
              ['swift', 'kotlin', 'objectivec', 'javaclient'].includes(
                formatLang,
              )
            ) {
              content = chunk.replace(
                /{{TERMINAL_PAYMENT_METHODS}}/,
                pmLines.join(','),
              );
            } else {
              return (
                <Chunk
                  key={`pms-${id}-${i}`}
                  id={id}
                  steps={steps}
                  language={language}
                  chunks={pmLines}
                />
              );
            }
          } else if (appearance && chunk.match(/{{APPEARANCE}}/)) {
            const appearanceLines = formatAppearanceObject(appearance);
            return (
              <Chunk
                key={`appearance-${id}-${i}`}
                id={id}
                steps={steps}
                language={language}
                chunks={appearanceLines}
              />
            );
          } else {
            content = chunk;
          }

          // Don't write a line if we don't have any content for it
          // This ensure lines that depend on dynamic values (like Terminal Location data)
          // won't render extra empty lines if the pref values don't have any data (e.g state value for Singapore)
          if (!content) {
            return;
          }

          return (
            <Line
              key={`${id}-${i}`}
              steps={steps}
              language={language || 'bash'}
              content={content}
              toggled={toggled}
              position={position}
            />
          );
        } else {
          // Then we have a Chunk within a Chunk and need to recurse
          return chunk;
        }
      })}
    </>
  );
};
